/* eslint-disable import/no-unresolved */
/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useCallback, useMemo, memo } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { useHistory } from "react-router-dom";
import * as Actions from "actions";
import {
  Box, Typography, Chip, IconButton,
} from "@mui/material";
import {getImage, getPortOrSquareImage} from "helpers/utils";

import "./UserTryOnItem.scss";
import HoverBox from "components/HoverBox";
import LazyImage from "components/LazyImage";
import { FlexBetween } from "components/flex-box";
import { H2, H6 } from "components/CustomTypography";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import DeleteIcon from "@mui/icons-material/Delete";

const UserTryOnItem = memo((props) => {
  const history = useHistory();

  const {
    tryon, link, onDelete, viewProductWithStory, isDirect, 
  } = props;

  // Get the first result image or fallback to product image (port or square)
  const tryonImage = useMemo(() => {
    if (tryon.result_images && tryon.result_images.length > 0) {
      return tryon.result_images[0]?.cdn?.secure_url || getImage(tryon.product_id?.images);
    }
    return getPortOrSquareImage(tryon.product_id?.images);
  }, [tryon.result_images, tryon.product_id?.images]);

  // Status icon and color
  const statusInfo = useMemo(() => {
    switch (tryon.status) {
      case 'completed':
        return { icon: <></>, color: 'success', label: '' };
      case 'processing':
        return { icon: <HourglassEmptyIcon />, color: 'warning', label: 'Processing' };
      case 'pending':
        return { icon: <HourglassEmptyIcon />, color: 'primary', label: 'Pending' };
      case 'failed':
        return { icon: <ErrorIcon />, color: 'error', label: 'Failed' };
      default:
        return { icon: <HourglassEmptyIcon />, color: 'default', label: tryon.status };
    }
  }, [tryon.status]);

  // Optimize callback functions with useCallback
  const handleTryOnClick = useCallback(() => {
    if (link) {
      history.push(link);
    } else {
      history.push(`/user/tryon/${tryon._id}`);
    }
  }, [link, history, tryon._id]);


    // Optimize click handlers
    const handleHoverBoxClick = useCallback(() => {
      if (isDirect) {
        handleTryOnClick();
      } else {
        viewProductWithStory();
      }
    }, [isDirect, handleTryOnClick, viewProductWithStory]);

  const handleDelete = useCallback((e) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(tryon._id);
    }
  }, [onDelete, tryon._id]);

  return (
    <Box pb={3} position="relative">
      <HoverBox mb={0} mx="auto" borderRadius="2px" onClick={handleHoverBoxClick}>
        <Box 
          className="tryon-image-container"
          sx={{
            position: 'relative',
            width: '100%',
            paddingTop: '177%', // 9:16 aspect ratio
            overflow: 'hidden',
            backgroundColor: '#f5f5f5',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '& img': {
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
                display: 'block',
              }
            }}
          >
            <LazyImage
              src={tryonImage}
              alt={tryon.product_id?.name || "Try-on result"}
              mockHeight={0}
            />
          </Box>
        </Box>
        <Box 
          position="absolute" 
          top={8} 
          right={8}
          zIndex={2}
        >
         {tryon.status !== "completed" && <Chip
            icon={statusInfo.icon}
            label={statusInfo.label}
            color={statusInfo.color}
            size="small"
            sx={{ 
              fontSize: '10px',
              height: '24px',
              backgroundColor: 'primary',
              backdropFilter: 'blur(4px)'
            }}
          />
}
        </Box>
        {onDelete && (
          <Box 
            position="absolute" 
            top={8} 
            left={8}
            zIndex={2}
          >
            <IconButton
              size="small"
              onClick={handleDelete}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(4px)',
                '&:hover': {
                  backgroundColor: 'rgba(244, 67, 54, 0.9)',
                  color: 'white'
                }
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </HoverBox>
      <FlexBetween mt={1} onClick={handleTryOnClick}>
        <Box width="100%">
          <H2 fontSize={14} textAlign="left" ellipsis>
            {tryon.product_id?.name || ""}
          </H2>
          <H6 textTransform="capitalize" fontSize={10} textAlign="left" color="textSecondary">
             {tryon.product_id?.price || ""}
          </H6>
        </Box>
      </FlexBetween>
    </Box>
  );
});

// Add display name for debugging
UserTryOnItem.displayName = 'UserTryOnItem';

UserTryOnItem.defaultProps = {
  link: null,
  onDelete: null,
};

UserTryOnItem.propTypes = {
  tryon: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    result_images: PropTypes.arrayOf(PropTypes.object),
    product_id: PropTypes.shape({
      title: PropTypes.string,
      name: PropTypes.string,
      images: PropTypes.array,
    }),
    created_at: PropTypes.string,
  }).isRequired,
  link: PropTypes.string,
  onDelete: PropTypes.func,
};

// Optimize Redux connection
const mapStateToProps = ({ bootupInfo }) => ({
  bootupInfo,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({ ...Actions }, dispatch);

// Use React.memo with custom comparison function for better performance
const areEqual = (prevProps, nextProps) => {
  return (
    prevProps.tryon._id === nextProps.tryon._id &&
    prevProps.tryon.status === nextProps.tryon.status &&
    prevProps.link === nextProps.link
  );
};

const MemoizedUserTryOnItem = memo(UserTryOnItem, areEqual);

const userTryOnItem = connect(
  mapStateToProps,
  mapDispatchToProps,
)(MemoizedUserTryOnItem);

export default userTryOnItem;
